/**
 * Where the bytes of a save actually go.
 *
 * `storage.ts` above this decides *what* a save is — the envelope, the two
 * worlds inside it, the version. This file decides *where it lands*, and it is
 * the only place in the project that knows there is more than one answer.
 *
 * There are two answers:
 *
 * - **`LocalStore`** — `localStorage`, exactly as the game has always used it.
 *   This is what a browser gets, and it must keep behaving precisely as it did,
 *   because `npm run dev` and `npm run shots` are the project's only way of
 *   looking at its own art. Slot 1 deliberately uses the *unchanged* key, so a
 *   save written by yesterday's build is found by today's without ceremony.
 * - **`FileStore`** — real files on disk, for the packaged desktop build. A
 *   campaign on a 190-province board serialises to something like 3.7 MB and a
 *   `localStorage` quota is five; the game was already at the wall.
 *
 * Both are reached through the same tiny synchronous interface, because the
 * callers are UI event handlers — a keypress, a menu click, an autosave inside
 * the frame loop — and none of them can be made to await. `FileStore` therefore
 * keeps every slot in memory and mirrors it to disk on a serialised queue.
 * `open()` fills that cache once, at boot, before the menu is drawn.
 *
 * Nothing here imports a platform API. The filesystem arrives as a `SaveFs`
 * handed in by the caller, which is what lets the whole of the interesting
 * logic — the writability probe, the fallback, the migration, the atomic
 * replace — be tested in Node with a fake disk. See `tests/savestore.test.ts`.
 */

/** How many save slots exist. One was the quota's decision, not a design one. */
export const SLOT_COUNT = 5;

/** The slot the game uses unless told otherwise — where every old save lives. */
export const DEFAULT_SLOT = 1;

export function isSlot(n: number): boolean {
  return Number.isInteger(n) && n >= 1 && n <= SLOT_COUNT;
}

export interface WriteResult {
  ok: boolean;
  bytes: number;
  error?: string;
}

export interface SaveStore {
  /** Which backend this is, for the player-facing line and for tests. */
  readonly kind: 'browser' | 'file';
  /** Where saves are going, in words a player can act on. */
  readonly location: string;
  /** The raw JSON of a slot, or null. Synchronous by contract. */
  read(slot: number): string | null;
  /**
   * Put `json` in `slot`. Synchronous by contract: the store is authoritative
   * the instant this returns, even if the disk has not caught up. Anything that
   * goes wrong later surfaces through `flush()`.
   */
  write(slot: number, json: string): WriteResult;
  remove(slot: number): void;
  /** Which slots hold something. */
  filled(): number[];
  /** Resolves when every queued write has actually landed. */
  flush(): Promise<WriteResult>;
}

// ── The filesystem, as little of it as this needs ────────────────────────────

/**
 * The filesystem seen from here.
 *
 * Deliberately six methods and no handles: everything below is expressible in
 * terms of them, and a fake that satisfies them fits in thirty lines of test.
 */
export interface SaveFs {
  mkdirp(dir: string): Promise<void>;
  exists(path: string): Promise<boolean>;
  readText(path: string): Promise<string>;
  writeText(path: string, text: string): Promise<void>;
  remove(path: string): Promise<void>;
  rename(from: string, to: string): Promise<void>;
}

/** Windows takes forward slashes everywhere; this saves an async `path.join`. */
export function joinPath(dir: string, name: string): string {
  return dir.endsWith('/') || dir.endsWith('\\') ? `${dir}${name}` : `${dir}/${name}`;
}

export function slotFile(dir: string, slot: number): string {
  return joinPath(dir, `slot-${slot}.json`);
}

// ── Choosing a directory ─────────────────────────────────────────────────────

export interface DirChoice {
  dir: string;
  /** Index into the candidate list that won, so the caller can say which. */
  index: number;
}

/**
 * The first candidate directory that can actually be written to.
 *
 * Portable-first: `./saves` beside the executable is candidate zero, because
 * that is what "copy the folder to a memory stick" means. But a folder under
 * `Program Files` is not writable without administrator rights, and the failure
 * mode of assuming otherwise is the worst one available — a game that looks
 * like it saved and did not. So each candidate is *proved* by creating it and
 * writing a real file into it, not by asking the operating system a question
 * about permissions that it will cheerfully answer wrongly.
 */
export async function chooseSaveDir(fs: SaveFs, candidates: string[]): Promise<DirChoice | null> {
  for (let i = 0; i < candidates.length; i++) {
    const dir = candidates[i];
    if (!dir) continue;
    const probe = joinPath(dir, '.writable');
    try {
      await fs.mkdirp(dir);
      await fs.writeText(probe, 'hearth');
      // Reading it back catches the case that bites hardest: Windows' installer
      // virtualisation, which accepts a write into a protected folder and files
      // it away somewhere the player will never find.
      const back = await fs.readText(probe);
      if (back !== 'hearth') continue;
      await fs.remove(probe).catch(() => {});
      return { dir, index: i };
    } catch {
      // Next candidate. An unwritable folder is expected, not exceptional.
      continue;
    }
  }
  return null;
}

// ── localStorage ─────────────────────────────────────────────────────────────

/** The subset of `Storage` this needs, so a test can hand over a fake. */
export interface KeyValue {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * The original backend, unchanged in behaviour.
 *
 * Slot 1 is stored under the bare save key. That is not tidiness, it is the
 * compatibility guarantee: every save ever written by this game lives at that
 * key, and a browser build must find it at that key today.
 */
export class LocalStore implements SaveStore {
  readonly kind = 'browser' as const;
  readonly location = 'browser storage';

  constructor(
    private readonly kv: KeyValue,
    private readonly baseKey: string,
  ) {}

  private key(slot: number): string {
    return slot === DEFAULT_SLOT ? this.baseKey : `${this.baseKey}.${slot}`;
  }

  read(slot: number): string | null {
    try {
      return this.kv.getItem(this.key(slot));
    } catch {
      return null;
    }
  }

  write(slot: number, json: string): WriteResult {
    try {
      this.kv.setItem(this.key(slot), json);
      return { ok: true, bytes: json.length };
    } catch (e) {
      // Quota is the usual culprit on a long run with a very built-up
      // settlement — and the reason the file backend exists at all.
      return { ok: false, bytes: 0, error: e instanceof Error ? e.message : String(e) };
    }
  }

  remove(slot: number): void {
    try {
      this.kv.removeItem(this.key(slot));
    } catch {
      // Nothing to be done about it.
    }
  }

  filled(): number[] {
    const out: number[] = [];
    for (let s = 1; s <= SLOT_COUNT; s++) if (this.read(s) !== null) out.push(s);
    return out;
  }

  flush(): Promise<WriteResult> {
    return Promise.resolve({ ok: true, bytes: 0 });
  }
}

// ── Files ────────────────────────────────────────────────────────────────────

/**
 * Saves as files, with the cache in front that lets the callers stay
 * synchronous.
 *
 * Writing is queued rather than concurrent. Two 3.7 MB writes racing for the
 * same slot — an autosave landing on the frame the player pressed F5 — could
 * otherwise interleave their renames and leave neither file whole.
 *
 * Each write is a replace, not an overwrite: new bytes go to `.tmp`, the old
 * file steps aside to `.bak`, and only then does `.tmp` take the real name. A
 * crash can therefore lose *the newest* save but never the one before it, and
 * `open()` reads `.bak` when the real file is missing or unreadable. Given that
 * a save here is several megabytes and the player may well be closing the
 * window as it lands, that is not a theoretical concern.
 */
export class FileStore implements SaveStore {
  readonly kind = 'file' as const;

  private readonly cache = new Map<number, string>();
  private queue: Promise<void> = Promise.resolve();
  private lastError: string | undefined;
  private pending = 0;

  constructor(
    private readonly fs: SaveFs,
    readonly dir: string,
    /** What to show the player. The real path, unless a caller prettifies it. */
    readonly location: string,
  ) {}

  /** Fill the cache from disk. Call once, before anything reads. */
  async open(): Promise<void> {
    for (let slot = 1; slot <= SLOT_COUNT; slot++) {
      const path = slotFile(this.dir, slot);
      const text = await this.readOne(path);
      if (text !== null) this.cache.set(slot, text);
    }
  }

  private async readOne(path: string): Promise<string | null> {
    for (const p of [path, `${path}.bak`]) {
      try {
        if (!(await this.fs.exists(p))) continue;
        const text = await this.fs.readText(p);
        // A truncated write leaves a file that exists and does not parse. The
        // backup is there precisely for this, so prove it before accepting it.
        JSON.parse(text);
        return text;
      } catch {
        continue;
      }
    }
    return null;
  }

  read(slot: number): string | null {
    return this.cache.get(slot) ?? null;
  }

  write(slot: number, json: string): WriteResult {
    this.cache.set(slot, json);
    this.enqueue(() => this.replace(slotFile(this.dir, slot), json));
    // Optimistic, and honest about it: the caller is told what it handed over,
    // and any disk failure is reported by `flush()` a moment later.
    return { ok: true, bytes: json.length, error: this.lastError };
  }

  remove(slot: number): void {
    this.cache.delete(slot);
    const path = slotFile(this.dir, slot);
    this.enqueue(async () => {
      await this.fs.remove(path).catch(() => {});
      await this.fs.remove(`${path}.bak`).catch(() => {});
    });
  }

  filled(): number[] {
    const out: number[] = [];
    for (let s = 1; s <= SLOT_COUNT; s++) if (this.cache.has(s)) out.push(s);
    return out;
  }

  /** True while bytes are still on their way to the disk. */
  get busy(): boolean {
    return this.pending > 0;
  }

  async flush(): Promise<WriteResult> {
    await this.queue;
    const error = this.lastError;
    this.lastError = undefined;
    return error ? { ok: false, bytes: 0, error } : { ok: true, bytes: 0 };
  }

  private enqueue(job: () => Promise<void>): void {
    this.pending++;
    this.queue = this.queue.then(async () => {
      try {
        await job();
      } catch (e) {
        this.lastError = e instanceof Error ? e.message : String(e);
      } finally {
        this.pending--;
      }
    });
  }

  private async replace(path: string, json: string): Promise<void> {
    const tmp = `${path}.tmp`;
    const bak = `${path}.bak`;
    await this.fs.writeText(tmp, json);
    if (await this.fs.exists(path)) {
      await this.fs.remove(bak).catch(() => {});
      await this.fs.rename(path, bak);
    }
    await this.fs.rename(tmp, path);
  }
}

// ── Migration ────────────────────────────────────────────────────────────────

export interface MigrationResult {
  moved: boolean;
  bytes: number;
  error?: string;
}

/**
 * Carry a browser save onto the disk, once.
 *
 * The condition is "the file store is empty and the browser store is not", which
 * makes this idempotent for free: the moment it succeeds, the file store is no
 * longer empty and it will never run again. Nothing is deleted from
 * `localStorage` — a player who somehow ends up back in the browser build still
 * has their run, and the cost of keeping it is a few megabytes of a quota that
 * the desktop build has stopped using.
 */
export function migrateToFiles(from: SaveStore, to: SaveStore): MigrationResult {
  if (to.filled().length > 0) return { moved: false, bytes: 0 };
  let moved = false;
  let bytes = 0;
  let error: string | undefined;
  for (let slot = 1; slot <= SLOT_COUNT; slot++) {
    const json = from.read(slot);
    if (json === null) continue;
    const r = to.write(slot, json);
    if (!r.ok) {
      error = r.error;
      continue;
    }
    moved = true;
    bytes += r.bytes;
  }
  return { moved, bytes, error };
}
